import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TrendingUp, Loader2, Sparkles } from "lucide-react";
import api from "../lib/api";

/**
 * DriverAnalyticsCard
 * ───────────────────
 * Weekly acquisition-source breakdown for the driver:
 *   - Reserved   → they claimed a scheduled ride early via /reserve
 *   - Broadcast  → they raced through top-5 dispatch and won
 *   - Random     → walk-ups, admin-assigned, etc.
 * Renders a horizontal stacked bar + per-source count + earnings so the
 * driver can see the tangible value of using the Reserve flow.
 */
export default function DriverAnalyticsCard() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/my/driver-analytics?days=7")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="mt-4 rounded-2xl border border-white/10 bg-obsidian/60 p-4 flex items-center justify-center min-h-[90px]" data-testid="driver-analytics-loading">
        <Loader2 className="animate-spin text-electric-teal" size={16} />
      </div>
    );
  }
  if (!data || data.total_rides === 0) return null;

  const src = data.by_source;
  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-obsidian/60 p-4" data-testid="driver-analytics-card">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-full bg-amber-gold text-obsidian flex items-center justify-center">
          <TrendingUp size={16} strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-black text-amber-gold">
            {t("dashboard.analyticsEyebrow", "Last {{n}} days", { n: data.window_days })}
          </div>
          <div className="text-sm font-black text-white">
            {t("dashboard.analyticsTitle", "How your rides come in")}
          </div>
        </div>
        <div className="text-end">
          <div className="text-lg font-black text-white tabular-nums">{data.total_rides}</div>
          <div className="text-[10px] text-white/50 uppercase">{t("dashboard.rides", "rides")}</div>
        </div>
      </div>

      {/* Stacked bar */}
      <div className="mt-2 h-2 rounded-full bg-white/[0.04] overflow-hidden flex" data-testid="driver-analytics-bar">
        {src.reserved.pct > 0 && (
          <div className="bg-electric-teal h-full" style={{ width: `${src.reserved.pct}%` }} title={`Reserved ${src.reserved.pct}%`} />
        )}
        {src.broadcast.pct > 0 && (
          <div className="bg-amber-gold h-full" style={{ width: `${src.broadcast.pct}%` }} title={`Broadcast ${src.broadcast.pct}%`} />
        )}
        {src.random.pct > 0 && (
          <div className="bg-white/25 h-full" style={{ width: `${src.random.pct}%` }} title={`Random ${src.random.pct}%`} />
        )}
      </div>

      {/* Legend rows */}
      <div className="mt-3 grid grid-cols-3 gap-2">
        {[
          { key: "reserved", label: t("dashboard.reserved", "Reserved"), dot: "bg-electric-teal", data: src.reserved },
          { key: "broadcast", label: t("dashboard.broadcast", "Broadcast"), dot: "bg-amber-gold", data: src.broadcast },
          { key: "random", label: t("dashboard.random", "Random"), dot: "bg-white/25", data: src.random },
        ].map((row) => (
          <div key={row.key} className="rounded-xl bg-white/[0.03] border border-white/10 p-2" data-testid={`analytics-row-${row.key}`}>
            <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase text-white/60">
              <span className={`w-1.5 h-1.5 rounded-full ${row.dot}`} /> {row.label}
            </div>
            <div className="mt-0.5 text-sm font-black text-white tabular-nums">{row.data.count}</div>
            <div className="text-[10px] text-white/50 tabular-nums">
              {row.data.earnings.toFixed(0)} {data.currency} · {row.data.pct}%
            </div>
          </div>
        ))}
      </div>

      {src.reserved.pct >= 20 && (
        <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-black text-electric-teal">
          <Sparkles size={11} /> {t("dashboard.analyticsInsight", "Reservations are winning you more rides — keep it up.")}
        </div>
      )}

      {/* Best time to drive — hourly earnings sparkline over the window.
          Renders only when we have at least one ride to plot. */}
      {(data.hourly || []).some((h) => h.count > 0) && (
        <div className="mt-4 border-t border-white/[0.06] pt-3" data-testid="best-time-to-drive">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest font-black text-white/50">
              {t("dashboard.bestTimeTitle", "Best time to drive")}
            </div>
            {(() => {
              const best = data.hourly.reduce((a, b) => (b.earnings > (a?.earnings || 0) ? b : a), null);
              if (!best || best.earnings === 0) return null;
              return (
                <div className="text-[10px] text-electric-teal font-black tabular-nums">
                  {String(best.hour).padStart(2, "0")}:00 · {best.earnings.toFixed(0)} {data.currency}
                </div>
              );
            })()}
          </div>
          <div className="flex items-end gap-[2px] h-10">
            {(() => {
              const max = Math.max(1, ...data.hourly.map((h) => h.earnings));
              return data.hourly.map((h) => {
                const pct = Math.round((h.earnings / max) * 100);
                return (
                  <div
                    key={h.hour}
                    className="flex-1 rounded-t-sm"
                    style={{
                      height: `${Math.max(pct, h.earnings > 0 ? 8 : 3)}%`,
                      background: h.earnings > 0 ? "linear-gradient(180deg,#00E5FF,rgba(0,229,255,0.35))" : "rgba(255,255,255,0.05)",
                    }}
                    title={`${String(h.hour).padStart(2, "0")}:00 · ${h.earnings.toFixed(0)} ${data.currency}`}
                    data-testid={`best-time-bar-${h.hour}`}
                  />
                );
              });
            })()}
          </div>
          <div className="mt-1 flex justify-between text-[9px] text-white/40 tabular-nums font-mono">
            <span>00</span><span>06</span><span>12</span><span>18</span><span>23</span>
          </div>
        </div>
      )}
    </div>
  );
}
